import { useEffect, useState } from "react";
import { apiClient } from "../api/apiClient.js";
import { listWorkerApplications } from "../workerApplications/workerApplicationsApi.js";
import { listEkycReviews } from "../ekycReviews/ekycReviewsApi.js";
import { listWorkerProfiles } from "../workerProfiles/workerProfilesApi.js";
import { listReadyForMatchCandidates } from "../readyForMatch/readyForMatchApi.js";
import { listJobs } from "../thailandJobs/thailandJobsApi.js";
import { listMatchingQueueJobs } from "../matchingQueue/matchingQueueApi.js";
import { listAssignments } from "../assignments/assignmentsApi.js";
import { listSosCases } from "../sos/sosApi.js";
import { listAbnormalEvents } from "../abnormal/abnormalApi.js";
import { listLatestTracking } from "../tracking/trackingApi.js";
import { listFakeLocationEvaluations } from "../fakeLocation/fakeLocationApi.js";
import { listFaceCheckDaily } from "../faceCheck/faceCheckApi.js";
import { listGeofenceZones } from "../geofence/geofenceApi.js";

// Every dashboard panel below the KPI strip and the /api/backoffice/home
// summary (visibleWorkerCount/visibleAgentCount/visibleEmployerCount/
// visibleJobCount, fetched separately by useBackofficeHome) is sourced from
// this fixed set of lightweight, already-shipped list endpoints - never a
// new backend route. Every call uses pageSize: 1 (or, for e-KYC, the one
// list call that already returns a `summary` block) purely to read back
// totalCount/summary - it never renders any row from these responses. Each
// feed's load/error state is tracked independently so one slow/broken
// endpoint (e.g. a Fake Location outage) never blocks or blanks out the
// rest of the dashboard - this is what Prompt 130 calls "handles partial
// API failure without making the whole dashboard look broken."
export const DASHBOARD_FEED_DEFINITIONS = [
  {
    key: "applications",
    label: "Worker Applications",
    call: () => listWorkerApplications({ pageSize: 1 }),
  },
  {
    key: "ekyc",
    // The one list call in this whole set whose response already carries a
    // `summary` block (EkycReviewSummaryDto: pendingReviewCount,
    // approvedCount, ...) - so this single pageSize:1 call covers both the
    // "e-KYC Pending" and "e-KYC Verified" funnel stages, no second call.
    label: "e-KYC Review Queue",
    call: () => listEkycReviews({ pageSize: 1 }),
  },
  {
    key: "workerProfiles",
    label: "Worker Profiles",
    call: () => listWorkerProfiles({ pageSize: 1 }),
  },
  {
    key: "readyForMatchQueue",
    label: "Ready for Match Queue",
    call: () => listReadyForMatchCandidates({ pageSize: 1 }),
  },
  {
    key: "jobsTotal",
    // Prompt 138X: the Job Demand chart's own "Jobs" bar - every job
    // demand record visible to the caller's scope, unfiltered by status.
    // Distinct from the KPI strip's "OPEN JOBS" (summary.visibleJobCount,
    // computed a different way via the hierarchy service) and from
    // jobsReadyForMatching below (demandStatus-filtered) - all three are
    // real, independently-sourced numbers, never derived from one another.
    label: "Jobs",
    call: () => listJobs({ pageSize: 1 }),
  },
  {
    key: "jobsReadyForMatching",
    label: "Jobs Ready for Matching",
    call: () => listJobs({ pageSize: 1, demandStatus: "ReadyForMatching" }),
  },
  {
    key: "matchingQueueJobs",
    label: "Matching Queue Jobs",
    call: () => listMatchingQueueJobs({ pageSize: 1 }),
  },
  {
    key: "assignmentsActive",
    label: "Active Assignments",
    call: () => listAssignments({ pageSize: 1, assignmentStatus: "Active" }),
  },
  {
    key: "sosOpen",
    label: "SOS Open Cases",
    call: () => listSosCases({ pageSize: 1, caseStatus: "Open" }),
  },
  {
    key: "abnormalOpen",
    label: "Abnormal Open Events",
    call: () => listAbnormalEvents({ pageSize: 1, status: "Open" }),
  },
  {
    key: "heartbeatMissing",
    label: "Missing Heartbeat",
    call: () => listLatestTracking({ pageSize: 1, heartbeatState: "Missing" }),
  },
  {
    key: "fakeLocationHigh",
    label: "Fake Location High Risk",
    call: () => listFakeLocationEvaluations({ pageSize: 1, riskLevel: "High" }),
  },
  {
    key: "faceCheckAttention",
    label: "Face Check Attention Required",
    call: () => listFaceCheckDaily({ pageSize: 1, complianceStatus: "AttentionRequired" }),
  },
  {
    key: "geofenceZonesActive",
    label: "Active Geofence Zones",
    call: () => listGeofenceZones({ pageSize: 1, zoneStatus: "Active" }),
  },
];

// Not a pageSize:1 list call - GET /api/backoffice/hierarchy/tree is the
// same real endpoint ../hierarchy/useHierarchyTree.js and
// SuperAgentsOverviewPage already use, reused here (own fetch, not the
// shared hook) so this hook stays the single fetch owner for the whole
// dashboard. Powers the Worker Line Attention panel and is also listed as
// one more row of the Operational Feeds panel.
export const HIERARCHY_FEED_KEY = "workerLineHierarchy";

function initialFeedState() {
  const feeds = {};
  for (const definition of DASHBOARD_FEED_DEFINITIONS) {
    feeds[definition.key] = { status: "loading", totalCount: null, summary: null, error: null };
  }
  feeds[HIERARCHY_FEED_KEY] = { status: "loading", data: null, error: null };
  return feeds;
}

/**
 * Fires every dashboard feed in parallel on mount and tracks each one's own
 * status independently - never a single all-or-nothing Promise.all, because
 * one broken feed must never blank out the panels backed by the others.
 */
export function useBackofficeDashboardData() {
  const [feeds, setFeeds] = useState(initialFeedState);

  useEffect(() => {
    let cancelled = false;
    setFeeds(initialFeedState());

    for (const definition of DASHBOARD_FEED_DEFINITIONS) {
      definition
        .call()
        .then((data) => {
          if (cancelled) {
            return;
          }
          setFeeds((previous) => ({
            ...previous,
            [definition.key]: {
              status: "ready",
              totalCount: typeof data?.totalCount === "number" ? data.totalCount : 0,
              summary: data?.summary ?? null,
              error: null,
            },
          }));
        })
        .catch((error) => {
          if (cancelled) {
            return;
          }
          setFeeds((previous) => ({
            ...previous,
            [definition.key]: { status: "error", totalCount: null, summary: null, error },
          }));
        });
    }

    apiClient
      .get(`/backoffice/hierarchy/tree?perspective=worker-intake`)
      .then((data) => {
        if (cancelled) {
          return;
        }
        setFeeds((previous) => ({
          ...previous,
          [HIERARCHY_FEED_KEY]: { status: "ready", data, error: null },
        }));
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }
        setFeeds((previous) => ({
          ...previous,
          [HIERARCHY_FEED_KEY]: { status: "error", data: null, error },
        }));
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return feeds;
}
